import { useState } from "react";
import { useDataProvider } from "ra-core";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { DEFAULT_ROUND_SIZE } from "./nearbyRound";
import { fetchNearbyStops } from "./nearbyRoundQuery";
import type { MapPoint } from "./directoryMapData";

export type NextRoundProps = {
  /** paradas de la ronda abierta, en el orden mostrado */
  stops: (MapPoint & { lat: number; lng: number })[];
  filter: Record<string, unknown>;
  size?: number;
  /** ids de rondas anteriores de esta misma sesión (no se repiten) */
  seen?: number[];
};

/**
 * "Siguiente ronda" (PLAN_RUTAS.md §B): mide desde la ÚLTIMA parada de la ronda abierta,
 * con el mismo filtro del Directorio, y excluye lo ya incluido (esta ronda y las
 * anteriores), las empresas con visita y las descartadas. "Quitar de esta ronda" no
 * cuenta como incluida: esa parada puede volver a salir.
 */
export const NextRoundButton = ({
  stops,
  filter,
  size,
  seen = [],
}: NextRoundProps) => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const last = stops[stops.length - 1];
  if (!last) return null;

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const allSeen = [...new Set([...seen, ...stops.map((s) => s.id)])];
      const n = size ?? DEFAULT_ROUND_SIZE;
      const next = await fetchNearbyStops({
        dataProvider,
        filter,
        center: last,
        n,
        excludeIds: new Set(allSeen),
      });
      if (next.length === 0) {
        setError("No hay más empresas cerca de la última parada.");
        return;
      }
      navigate("/directory/ruta", {
        replace: true,
        state: {
          stops: next,
          start: { lat: last.lat, lng: last.lng },
          filter,
          size: n,
          seen: allSeen,
        },
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <Button className="h-12" disabled={busy} onClick={run}>
        {busy ? "Buscando…" : "Siguiente ronda desde la última parada"}
      </Button>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};
